// i18next initialization — the single source of truth for app localization.
// Imported once for its side effect (in src/index.js, before <App/>); it wires the
// default i18next instance that react-i18next's useTranslation() reads everywhere.
//
// Decisions (see ARCHITECTURE.md ADR #13 and LOCALIZATION.md):
//   - HTTP backend lazy-loads /locales/<lng>/<ns>.json from CRA's public/ folder, so
//     translators can edit JSON without a rebuild. All languages are preloaded so the
//     footer switch is instant (no fetch on toggle).
//   - LanguageDetector resolves the initial language from localStorage (STORAGE_KEYS.LANG)
//     then the browser, and caches the chosen language back to localStorage.
//   - fallbackLng 'en' + supportedLngs lock the app to the languages we ship today;
//     adding a language later is a new locales/<lng>/ folder + one supportedLngs entry.
//   - useSuspense:false — we gate the first paint in LanguageProvider instead, and avoid
//     a Suspense blank on real-time switching.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { STORAGE_KEYS } from '../config/constants';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'zh', label: '中文' },
];

export const DEFAULT_LANGUAGE = 'en';

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // 'es-MX' → 'es'
    load: 'languageOnly', // ignore region; we ship language-level packs
    preload: SUPPORTED_LANGUAGES.map((l) => l.code), // all fetched up front → instant switch
    ns: ['translation'],
    defaultNS: 'translation',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json', // served from public/locales
      // …plus a build stamp. These files are the ONE thing CRA does not hash: public/ is
      // copied verbatim, so the URL is stable while the contents change every release.
      // A browser that cached the pack before a deploy kept serving it and every key
      // added since came back as its own key string — mobile-only wherever the phone
      // label is a newer key than the desktop one (Recent activity's footer doors).
      // The query makes each build's request a different URL, so a stale entry cannot be
      // hit at all; nginx's `no-cache` on /locales/ (nginx.conf) is the other half.
      // CI passes the commit SHA; a local build has no cache to poison, hence 'dev'.
      queryStringParams: { v: process.env.REACT_APP_BUILD_ID || 'dev' },
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEYS.LANG, // 'pcs_lang' — same key the legacy footer reserved
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
    returnEmptyString: false, // empty value → fall back, never render ''
    react: {
      useSuspense: false,
    },
    // Surface missing keys in development so gaps are caught before QA. saveMissing
    // only triggers the handler locally; the HTTP backend has no addPath, so nothing
    // is ever written back — this is pure console diagnostics.
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler:
      process.env.NODE_ENV === 'development'
        ? (lngs, ns, key) => {
            // eslint-disable-next-line no-console
            console.warn(`[i18n] missing key "${key}" (ns: ${ns}, lng: ${lngs.join(',')})`);
          }
        : undefined,
  });

export default i18n;
